import { ApolloError } from '@apollo/client';
import { useCallback } from 'react';
import { useSalesPortalCreateActivityReminderMutation } from '@/experiences/sales-portal/blocs/documents/sales-portal-create-activity-reminder.generated';

export interface CreateActivityReminderParams {
  chatUUID: string;
  crmActivityUUID: string;
  reminderDatetime: Date;
}

export interface CreateActivityReminderMutatorOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

export interface CreateActivityReminderMutatorResult {
  createReminder: (params: CreateActivityReminderParams) => Promise<boolean>;
  error: ApolloError | undefined;
  loading: boolean;
}

/**
 * Mutator to create an activity reminder.
 *
 * @param options.onError - Callback invoked when the mutation fails
 * @param options.onSuccess - Callback invoked when the mutation succeeds
 *
 * @returns {@link CreateActivityReminderMutatorResult}
 *
 * @example
 * ```typescript
 * const { createReminder, loading } = useCreateActivityReminderMutator({
 *   onSuccess: () => showToast({ title: 'Reminder set', variant: 'success' }),
 *   onError: (err) => showErrorToast(err.message),
 * });
 * await createReminder({
 *   chatUUID: 'chat-123',
 *   crmActivityUUID: 'activity-456',
 *   reminderDatetime: new Date('2024-01-15T10:00:00'),
 * });
 * ```
 */
export const useCreateActivityReminderMutator = (
  options?: CreateActivityReminderMutatorOptions,
): CreateActivityReminderMutatorResult => {
  const [createActivityReminderMutation, { loading, error }] =
    useSalesPortalCreateActivityReminderMutation();

  const createReminder = useCallback<
    (params: CreateActivityReminderParams) => Promise<boolean>
  >(
    async (params) => {
      const { chatUUID, crmActivityUUID, reminderDatetime } = params;

      try {
        await createActivityReminderMutation({
          variables: {
            chatUUID,
            crmActivityUUID,
            reminderDatetime: reminderDatetime.toISOString(),
          },
        });

        options?.onSuccess?.();
        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        options?.onError?.(error);
        return false;
      }
    },
    [createActivityReminderMutation, options],
  );

  return {
    createReminder,
    error,
    loading,
  };
};
